import React, { useEffect } from 'react';
import classnames from 'classnames/bind';
import { useDocumentTitle } from '../../shared/use-document-title';
import { Input } from '../../components/Input';
import { Popover } from '../../components/Popover';
import { Button } from '../../components/Button';
import { SafetyIndicator } from '../../components/SafetyIndicator';
import { Loader } from '../../components/Loader';
import { useSearchForm } from '../../shared/use-search-form';
import { checkReputation } from '../../../lib/use-cases/check-reputation';
import { useRouting } from '../../shared/use-routing';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import { RISK_STATUS } from '../../../lib/shared/reputation-risk-status';
import styles from './ReputationCheckPage.css';

const cx = classnames.bind(styles);

const renderBooleanValue = (bool, withError = true) => <span {...(bool && withError ? {className: styles.errorText} : {})}>{bool ? 'Yes' : 'No'}</span>;

function ReputationCheckPage() {
    useDocumentTitle('Link Reputation checker');

    const {parseLocationSearch, location, navigate} = useRouting();
    const initialParams = parseLocationSearch(location.search);
    const {errors, fields, changeField, submitForm, clearForm, isPending, data} = useSearchForm(checkReputation, () => logEvent(EVENT_TYPES.REPUTATION_CHECK_REQUESTED), {...initialParams, data: {}});

    useEffect(() => {
        if (initialParams.searchText) {
            submitForm();
            navigate({search: ''}, {replace: true});
        }
    }, []);

    const renderTableHeadAndCommonRows = isBlacklisted => (
        <>
            <div className={styles.tableHead}>
                <span>Feature</span>
                <span>Risk aspect</span>
            </div>
            <div className={styles.tableRow}>
                <Popover description={data.type === 'email' ? 'A high score means the email is likely dangerous or used for scams or spam.' : 'A high score means the website is very likely dangerous or malicious.'}>
                    <span>Risk Score</span>
                </Popover>
                <div className={styles.separator} />
                <span className={styles.riskText}>{data.riskStatus}</span>
            </div>
            {data.type === 'email'
                ? (
                    <div className={styles.tableRow}>
                        <Popover description='If "Yes", the system recommends blocking this email — strong warning sign.'>
                            <span>Should Block</span>
                        </Popover>
                        <div className={styles.separator} />
                        {renderBooleanValue(data.shouldBlock)}
                    </div>
                )
                : (
                    <div className={styles.tableRow}>
                         <Popover description={data.type === 'domain' ? 'If even one security engine flags the domain, it may be considered harmful or suspicious. More detections = higher risk.' : 'If blacklisted, the website is already known as harmful or suspicious by trusted threat databases.'}>
                            <span>Blacklisted</span>
                        </Popover>
                        <div className={styles.separator} />
                        {renderBooleanValue(isBlacklisted)}
                    </div>
                )
            }
        </>
    );

    const renderResults = () => {
        if (isPending) {
            return <Loader variant='secondary' className={styles.loader} />;
        }

        if (!data.type) {
            return (
                <p className={styles.noteBlock}>
                    <strong>{fields.searchText.trim() === '' ? 'Empty link, website, or email address' : 'No valid link, website, or email detected'}.</strong> <br /><br />
                    Please enter a valid URL or email (e.g., example@domain.com) to check for reputation.
                </p>
            );
        }

        if (data.type === 'domain') {
            return (
                <>
                    <h2 className={styles.secondaryTitle}>Domain Reputation Details</h2>
                    <p className={styles.description}>
                        <strong>Domain Reputation</strong> checks if a domain is blacklisted and evaluates its reputation. It helps identify potentially harmful domains linked to phishing or fraud.
                    </p>
                    <section className={styles.resultSection}>
                        <div className={styles.table}>
                            {renderTableHeadAndCommonRows(data.isBlacklisted)}
                            <div className={styles.tableRow}>
                                <Popover description='If marked as risky, the domain behaves like known malicious or phishing domains — proceed with caution.'>
                                    <span>Risky Category</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isRiskyCategory)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`Some extensions (like .xyz, .top, etc.) are frequently used in scams. If this one is on that list, it's worth being cautious.`}>
                                    <span>Suspicious Domain Extension</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isMostAbusedTld)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`Website popularity shows how well-known the domain is across the internet. If the value is low it may indicate a new, obscure, or suspicious website.`}>
                                    <span>Website Popularity</span>
                                </Popover>
                                <div className={styles.separator} />
                                <span>{data.popularity}</span>
                            </div>
                        </div>
                    </section>
                </>
            )   
        }

        if (data.type === 'email') {
            return (
                <>
                    <h2 className={styles.secondaryTitle}>Email Reputation Details</h2>
                    <p className={styles.description}>
                        <strong>Email Reputation</strong> verifies the reputation of an email address to determine if it is risky or suspicious. It is useful for filtering out unwanted or potentially harmful addresses in mailing lists and databases.
                    </p>
                    <section className={styles.resultSection}>
                        <div className={styles.table}>
                            {renderTableHeadAndCommonRows()}
                            <div className={styles.tableRow}>
                                <Popover description={`If "Yes", someone could easily send fake emails pretending to be from this address.`}>
                                    <span>Can Be Faked</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isSpoofable, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If the email syntax is invalid.`}>
                                    <span>Invalid</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(!data.isValid, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If the email’s domain has poor protection, it’s easier for attackers to impersonate it.`}>
                                    <span>Weak Security</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.weakSecurity, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If "Yes", this is a throwaway email — often used by spammers or scammers.`}>
                                    <span>Temporary Email</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isDisposable, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If "Yes", it’s from a free service (like Gmail or Yahoo). Not always bad, but more often abused.`}>
                                    <span>Free Email</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isFree, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`The domain part (after the "@") looks suspicious or similar to scam websites.`}>
                                    <span>Suspicious Domain</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isDomainSuspicious, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`The email address shows signs of being untrustworthy, even if some parts seem okay.`}>
                                    <span>Suspicious Overall</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isEmailSuspicious, false)}
                            </div>
                        </div>
                    </section>
                </>
            )   
        }

        if (data.type === 'url') {
            return (
                <>
                    <h2 className={styles.secondaryTitle}>URL Reputation Details</h2>
                    <p className={styles.description}>
                        <strong>URL Reputation</strong> determines if a specific URL is malicious or suspicious. It analyzes the URL and provides a risk score to help users avoid dangerous websites.
                    </p>
                    <section className={styles.resultSection}>
                        <div className={styles.table}>
                            {renderTableHeadAndCommonRows(data.isDomainBlacklisted)}
                            <div className={styles.tableRow}>
                                <Popover description={`The website behaves like a scam, phishing, or malware site — it's not to be trusted.`}>
                                    <span>Suspicious Category</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isRiskyDomainCategory, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`Indicates whether the URL contains a file extension commonly associated with malware or malicious activity (e.g., .exe, .js, .scr). If true, it suggests the link may point to a potentially dangerous file.`}>
                                    <span>Suspicious Domain Extension</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isMostAbusedTld, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If "Yes", your data is encrypted during transfer. If "No", your information could be exposed.`}>
                                    <span>Secure Connection</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isValidHttps, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`A "Yes" means the link uses a URL shortening service (like bitly or tinyurl). Shortened links hide the final destination, which can be used to trick users into clicking on malicious or misleading websites.`}>
                                    <span>Shortened URL</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isUrlShortener, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`Tells if the link sends you somewhere else. "High" means it may lead to a different, possibly unsafe website.`}>
                                    <span>Redirect risk</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isExternalRedirect, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`A "Yes" means the website may be pretending to be another trusted site to steal your data.`}>
                                    <span>Phishing Signs</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.isLikelyPhishing, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If the site asks for a password and has a bad reputation, it could be trying to steal your login.`}>
                                    <span>Asks for Password</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.containsPasswordField, false)}
                            </div>
                            <div className={styles.tableRow}>
                                <Popover description={`If a shady site requests payment info — that’s a major red flag for fraud.`}>
                                    <span>Asks for Credit Card</span>
                                </Popover>
                                <div className={styles.separator} />
                                {renderBooleanValue(data.containsCreditCardField, false)}
                            </div>
                        </div>
                    </section>
                </>
            )   
        }
    };

    return (
        <div className={cx('root', {[data?.riskStatus]: !isPending && Boolean(data?.riskStatus)})}>
            <section className={styles.section}>
                <div className={styles.wrapper}>
                    <h1 className={styles.title}>Link Reputation Checker</h1>
                    <p className={styles.description}>
                        Quickly find out if a website or email poses a threat — one check for peace of mind.
                    </p>
                    <form className={styles.form}>
                        <Input
                            name='searchText'
                            onChange={changeField}
                            placeholder='Enter a link, website, or email'
                            value={fields.searchText}
                            errorText={errors.searchText || errors.form}
                            className={styles.input}
                            onClear={clearForm}
                        />
                        <Button
                            className={styles.button}
                            onClick={submitForm}
                            disabled={isPending}
                            label='Check it'
                        />
                    </form>
                    {Boolean(data.type) && !isPending
                        ? (
                            <div className={styles.riskBlock}>
                                <SafetyIndicator
                                    description={`${data.riskStatus === RISK_STATUS.HIGH ? 'Warning! ' : ''}${data.riskStatus} risk score ${data.riskScore} of 100`}
                                    status={data.riskStatus}
                                />
                                {data.riskStatus !== RISK_STATUS.LOW && (
                                    <p className={styles.riskBlockText}>
                                        {data.type === 'email'
                                            ? <>Block and do not engage! <br /> Avoid interacting with email addresses that have a non-zero risk score. <br /> High risk scores indicate potential threats.</>
                                            : <>Recommendation: Avoid interacting with risky resources. <br /> High risk scores require expert knowledge and protection!</>
                                        }
                                    </p>
                                )}
                            </div>
                        )
                        : <div />
                    }
                </div>
            </section>
            {renderResults()}
        </div>
    )
};

export {
    ReputationCheckPage
};
